/**
 * Every billing plan, cheapest first.
 *
 * The order is the upgrade order: `getPlanOrder` reads this array, so a plan
 * added here is ordered by where it is written rather than by a second table
 * that can disagree with it.
 *
 * `enterprise` is the one plan with no published price (task 345). It holds
 * every capability and Business's limits as a floor, and a contract raises
 * those limits per tenant through `tenants`' override columns. Nothing sells
 * it: checkout, the plan-change route, the pricing page and the onboarding
 * cards all work from `PUBLISHED_PLANS` below.
 */
export const PLANS = [
    "free",
    "team",
    "professional",
    "business",
    "enterprise",
];
/**
 * The plans with a published price: what the pricing page prints, what
 * checkout sells, and what the onboarding tier cards offer.
 *
 * Enterprise is deliberately absent. Its price is a contract and its limits
 * are per tenant, so a page that printed "Enterprise: 750,000 reports" would
 * be stating a floor as an allowance.
 */
export const PUBLISHED_PLANS = [
    "free",
    "team",
    "professional",
    "business",
];
/** The plans that cost money. Enterprise is one; it is just not self-serve. */
export const PAID_PLANS = [
    "team",
    "professional",
    "business",
    "enterprise",
];
/**
 * The paid plans checkout and the plan-change route accept. Enterprise is
 * reached only by a Stripe subscription against its contract's own price
 * (`headerhawk_plan=enterprise`), never by a request naming the plan.
 */
export const SELF_SERVE_PLANS = [
    "team",
    "professional",
    "business",
];
/** Where a plan sits in the upgrade order; higher is more premium. */
export function getPlanOrder(plan) {
    return PLANS.indexOf(plan);
}
/** Whether moving from `currentPlan` to `newPlan` is an upgrade. */
export function isPlanUpgrade(currentPlan, newPlan) {
    return getPlanOrder(newPlan) > getPlanOrder(currentPlan);
}
/** Whether a string names a plan a customer can buy through checkout. */
export function isSelfServePlan(value) {
    return SELF_SERVE_PLANS.includes(value);
}
/**
 * Whether a plan comes with the free trial: every self-serve plan, since task
 * 402 (only Team before it). A customer still gets one trial, not one per
 * plan; the guards are per tenant and per email (`apps/dashboard-api`'s
 * `lib/trial.ts`). Enterprise has none, because a contract is not a checkout.
 */
export function planHasTrial(value) {
    return isSelfServePlan(value);
}
/** Whether a string names a plan that costs money, Enterprise included. */
export function isPaidPlan(value) {
    return PAID_PLANS.includes(value);
}
/**
 * How often a self-serve plan is billed, as checkout and the pricing page say
 * it. Stripe's own words are `month` and `year`.
 */
export const BILLING_INTERVALS = ["monthly", "annual"];
/**
 * Whether a value names a billing interval. The dashboard API reports one as
 * a plain string, since a nullable enum cannot pass API Gateway's import.
 */
export function isBillingInterval(value) {
    return BILLING_INTERVALS.includes(value);
}
/**
 * When a change of plan, billing interval or both takes effect (task 412).
 *
 * It turns on what the customer has already paid for. A higher plan applies
 * now, with a credit for the unused part of the period, and so does a move
 * from monthly to annual, which starts a new yearly period today. A lower
 * plan waits for the end of the period (task 324), and so does a move from
 * annual to monthly: switching at once would take away, or refund as credit,
 * the rest of a year already paid for.
 *
 * A change of both follows the stricter of the two. Anything that lowers the
 * plan or moves to monthly waits, even when the other half would apply now.
 * The plan-change route acts on this and the billing page's confirmation
 * dialog describes it, so the two cannot disagree.
 */
export function planChangeTiming(from, to) {
    const lowersPlan = getPlanOrder(to.plan) < getPlanOrder(from.plan);
    const toMonthly = from.interval === "annual" && to.interval === "monthly";
    return lowersPlan || toMonthly ? "period_end" : "now";
}
/**
 * The lookup key of the Stripe price a self-serve plan is sold at now
 * (task 413).
 *
 * Checkout, the plan-change route and operator trials find the price to
 * charge by this key rather than by an id, because a reprice creates a new
 * price: Terraform (`infra/stripe/catalog.tf`) gives the new price the key
 * and takes it from the old one in the same call. The format is written out
 * there too, and `apps/dashboard-api/tests/stripe-catalog.test.ts` holds the
 * two together.
 */
export function stripePriceLookupKey(plan, interval) {
    return `headerhawk_${plan}_${interval}`;
}
//# sourceMappingURL=entities.js.map